/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NavigationModule
 */

import {
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';

import {
    VS_DETAIL_ALERTS,
    VS_DETAIL_ANALYTICS,
    VS_DETAIL_DNS_RECORDS,
    VS_DETAIL_EVENTS,
    VS_DETAIL_HS_INSIGHTS,
    VS_DETAIL_INSIGHTS,
    VS_DETAIL_SECURITY,
    VS_DETAIL_TOPOLOGY,
    VS_DETAIL_VS_LOGS,
    VS_VS_DETAIL,
} from 'state-names';

import * as globalL10n from 'global-l10n';
import { NavigationService } from '../../services/navigation.service';
import type { ISubheaderNavState } from '../subheader-nav/subheader-nav.component';
import * as l10n from './virtual-service-subheader.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Subheader component for Virtual Service detail pages.
 * @author alextsg
 */
@Component({
    selector: 'virtual-service-subheader',
    template: '<subheader-nav [states]=states><ng-content></ng-content></subheader-nav>',
})
export class VirtualServiceSubheaderComponent implements OnInit, OnDestroy {
    /**
     * Current VS, set to "any" as the type since the VS item has not yet been migrated.
     */
    @Input()
    public vs: any;

    public states: ISubheaderNavState[] = [];

    /**
     * Hash of state names to their labels.
     */
    public labelsHash: Record<string, string> = {};

    constructor(
        private readonly navigationService: NavigationService,
        l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.labelsHash = {
            [VS_DETAIL_TOPOLOGY]: l10nService.getMessage(l10nKeys.topologyLabel),
            [VS_DETAIL_ANALYTICS]: l10nService.getMessage(globalL10nKeys.analyticsLabel),
            [VS_DETAIL_VS_LOGS]: l10nService.getMessage(globalL10nKeys.logsLabel),
            [VS_DETAIL_HS_INSIGHTS]: l10nService.getMessage(globalL10nKeys.healthLabel),
            [VS_DETAIL_SECURITY]: l10nService.getMessage(globalL10nKeys.securityLabel),
            [VS_DETAIL_EVENTS]: l10nService.getMessage(globalL10nKeys.eventsLabel),
            [VS_DETAIL_ALERTS]: l10nService.getMessage(globalL10nKeys.alertsLabel),
            [VS_DETAIL_DNS_RECORDS]: l10nService.getMessage(globalL10nKeys.dnsLabel),
            [VS_DETAIL_INSIGHTS]: l10nService.getMessage(globalL10nKeys.insightsLabel),
        };
    }

    /** @override */
    public ngOnInit(): void {
        this.setNavLinks();

        this.vs.on('itemSaveSuccess', this.setNavLinks);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.vs.unbind('itemSaveSuccess', this.setNavLinks);
    }

    /**
     * Track by function for ngFor iterator on states.
     */
    public trackByState(index: number, state: ISubheaderNavState): string {
        return state.name;
    }

    /**
     * Set nav links for the VS, which may change when the VS config is updated. For example, the
     * WAF link is visible only for VSes with WAF configured, and the user could add/remove a WAF
     * Policy from the VS while this component is shown.
     */
    private setNavLinks = (): void => {
        this.states = this.navigationService.getChildren(VS_VS_DETAIL)
            .filter(state => this.showState(state.name))
            .map(state => ({
                ...state,
                label: this.labelsHash[state.name],
            }));
    };

    /**
     * Show or hide states by how the VS is configured.
     */
    private showState(stateName: ISubheaderNavState['name']): boolean {
        switch (stateName) {
            case VS_DETAIL_DNS_RECORDS:
                return this.vs.isDNS();
            default:
                return true;
        }
    }
}
